import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { PRODUCTS, WHATSAPP_URL, PHONE, ADDRESS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    product_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/leads`, payload);
      toast.success("Enquiry received. We'll reach out shortly.");
      setForm({ name: "", phone: "", email: "", product_interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border py-4 text-lg placeholder:text-foreground/30 focus:border-accent focus:outline-none transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-40 bg-[#FFFFFF]">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <p className="overline mb-6">Enquire</p>
          <h2 className="font-heading font-light text-5xl md:text-7xl tracking-tight leading-[0.95] mb-10">
            Let's build your
            <br />
            <span className="italic text-accent">perfect sleep.</span>
          </h2>
          <p className="text-base md:text-lg text-foreground/60 max-w-md mb-12">
            Tell us what you need — a mattress, custom foam, or bulk order. Our
            team responds personally.
          </p>

          <div className="space-y-6 text-sm">
            <div>
              <p className="overline mb-2">Call / WhatsApp</p>
              <a href={`tel:${PHONE}`} className="text-lg hover:text-accent transition-colors" data-testid="contact-phone">
                {PHONE}
              </a>
            </div>
            <div>
              <p className="overline mb-2">Factory & Store</p>
              <p className="text-base text-foreground/70 max-w-xs leading-relaxed">{ADDRESS}</p>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="inline-flex items-center gap-2 text-accent hover:underline"
            >
              Chat on WhatsApp →
            </a>
          </div>
        </div>

        {/* Form */}
        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 lg:pl-12 space-y-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <input
              className={inputCls}
              placeholder="Your name *"
              value={form.name}
              onChange={update("name")}
              data-testid="contact-name-input"
            />
            <input
              className={inputCls}
              placeholder="Phone number *"
              value={form.phone}
              onChange={update("phone")}
              data-testid="contact-phone-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <input
              type="email"
              className={inputCls}
              placeholder="Email (optional)"
              value={form.email}
              onChange={update("email")}
              data-testid="contact-email-input"
            />
            <Select
              value={form.product_interest}
              onValueChange={(v) => setForm((f) => ({ ...f, product_interest: v }))}
            >
              <SelectTrigger
                data-testid="contact-product-select"
                className="w-full bg-transparent border-0 border-b border-border rounded-none py-4 h-auto text-lg data-[placeholder]:text-foreground/30 focus:ring-0 focus:border-accent px-0"
              >
                <SelectValue placeholder="Product interest" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {PRODUCTS.map((p) => (
                  <SelectItem key={p.id} value={p.name} data-testid={`product-option-${p.id}`}>
                    {p.name}
                  </SelectItem>
                ))}
                <SelectItem value="Bulk / Custom">Bulk / Custom order</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <textarea
            rows={3}
            className={`${inputCls} resize-none`}
            placeholder="Tell us more (size, quantity, requirements)"
            value={form.message}
            onChange={update("message")}
            data-testid="contact-message-input"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-foreground text-primary-foreground text-sm tracking-wide hover:bg-accent transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send enquiry"}
            <span aria-hidden>→</span>
          </button>
        </motion.form>
      </div>
    </section>
  );
};
